import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Tabs,
    Tab,
    Avatar,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Alert,
    FormControlLabel,
    Switch,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import QuillEditor from "../../../components/QuillEditor";

const languages = ['en', 'ru', 'hy'];
const languageLabels = { en: 'English', ru: 'Russian', hy: 'Armenian' };

const initialForm = () => {
    const f = {
        thumbnail: null,
        price: '',
        original_price: '',
        discount: '0',
        category_id: '',
        total_lessons: '',
        duration: '',
        is_private: false,
    };
    languages.forEach(lang => {
        f[`name_${lang}`] = '';
        f[`description_${lang}`] = '';
        f[`body_${lang}`] = '';
    });
    return f;
};

export default function CourseModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(initialForm);
    const [imagePreview, setImagePreview] = useState(null);
    const [deleteThumbnail, setDeleteThumbnail] = useState(false);
    const [categories, setCategories] = useState([]);

    const currentLanguage = languages[activeTab];

    useEffect(() => {
        if (open) {
            api.get('admin/categories/list').then((res) => {
                setCategories(res.data.data || []);
            }).catch(() => setCategories([]));
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleBodyChange = (language, value) => {
        setForm((prev) => ({ ...prev, [`body_${language}`]: value }));
    };

    const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

    const handlePricingChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => {
            const next = { ...prev, [name]: value };

            let original = parseFloat(name === "original_price" ? value : next.original_price);
            let price = parseFloat(name === "price" ? value : next.price);
            let discount = parseFloat(name === "discount" ? value : next.discount);

            if (name === "discount") {
                if (!Number.isNaN(original) && original > 0 && !Number.isNaN(discount)) {
                    const boundedDiscount = Math.min(100, Math.max(0, discount));
                    next.discount = String(boundedDiscount);
                    next.price = String(roundToTwo(original - (original * boundedDiscount) / 100));
                }
            } else if (name === "original_price") {
                if (next.discount === '' || Number.isNaN(discount)) {
                    next.discount = '0';
                    discount = 0;
                }
                if (!Number.isNaN(original) && original >= 0) {
                    if (Number(discount) === 0) {
                        next.price = String(roundToTwo(original));
                    } else if (!Number.isNaN(price)) {
                        const calculatedPrice = original - (original * Number(discount)) / 100;
                        next.price = String(roundToTwo(calculatedPrice));
                    }
                }
            } else if (name === "price") {
                if (next.discount === '' || Number.isNaN(discount)) {
                    next.discount = '0';
                    discount = 0;
                }
                if ((next.original_price === '' || Number.isNaN(original)) && !Number.isNaN(price)) {
                    next.original_price = String(price);
                    original = price;
                }
                if (!Number.isNaN(original) && original > 0 && !Number.isNaN(price)) {
                    const calculatedDiscount = ((original - price) / original) * 100;
                    const boundedDiscount = Math.min(100, Math.max(0, calculatedDiscount));
                    next.discount = String(roundToTwo(boundedDiscount));
                }
            } else if (!Number.isNaN(original) && original > 0 && !Number.isNaN(price)) {
                if (next.discount === '' || Number.isNaN(discount)) {
                    next.discount = '0';
                }
                const calculatedDiscount = ((original - price) / original) * 100;
                const boundedDiscount = Math.min(100, Math.max(0, calculatedDiscount));
                next.discount = String(roundToTwo(boundedDiscount));
            } else if (name === "original_price" && (value === "" || Number(value) <= 0)) {
                next.discount = "0";
            }

            return next;
        });
    };

    const tabHasErrors = (lang) => Boolean(
        errors[`name_${lang}`] ||
        errors[`description_${lang}`] ||
        errors[`body_${lang}`]
    );

    const errorMessages = Object.values(errors)
        .flat()
        .filter(Boolean);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({ ...prev, thumbnail: file }));
            setDeleteThumbnail(false);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setForm((prev) => ({ ...prev, thumbnail: null }));
        setImagePreview(null);
        if (row?.thumbnail) setDeleteThumbnail(true);
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            const hasThumbnailFile = form.thumbnail instanceof File;
            const isUpdate = !!row?.id;

            if (isUpdate && (hasThumbnailFile || deleteThumbnail)) {
                const formData = new FormData();
                languages.forEach(lang => {
                    formData.append(`name_${lang}`, form[`name_${lang}`] ?? '');
                    formData.append(`description_${lang}`, form[`description_${lang}`] ?? '');
                    formData.append(`body_${lang}`, form[`body_${lang}`] ?? '');
                });
                formData.append('price', form.price);
                formData.append('original_price', form.original_price ?? '');
                formData.append('discount', form.discount ?? '');
                formData.append('category_id', form.category_id ?? '');
                formData.append('total_lessons', form.total_lessons ?? '');
                formData.append('duration', form.duration ?? '');
                formData.append('is_private', form.is_private ? '1' : '0');
                if (hasThumbnailFile) formData.append('thumbnail', form.thumbnail);
                if (deleteThumbnail) formData.append('delete_thumbnail', '1');
                await api.post(`admin/courses/${row.id}/update`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else if (!isUpdate && hasThumbnailFile) {
                const formData = new FormData();
                languages.forEach(lang => {
                    formData.append(`name_${lang}`, form[`name_${lang}`] ?? '');
                    formData.append(`description_${lang}`, form[`description_${lang}`] ?? '');
                    formData.append(`body_${lang}`, form[`body_${lang}`] ?? '');
                });
                formData.append('price', form.price);
                formData.append('original_price', form.original_price ?? '');
                formData.append('discount', form.discount ?? '');
                formData.append('category_id', form.category_id ?? '');
                formData.append('total_lessons', form.total_lessons ?? '');
                formData.append('duration', form.duration ?? '');
                formData.append('is_private', form.is_private ? '1' : '0');
                formData.append('thumbnail', form.thumbnail);
                const response = await api.post('admin/courses', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                onClose(true, response?.data?.data || null);
                return;
            } else if (!isUpdate) {
                const payload = {
                    name_en: form.name_en,
                    name_ru: form.name_ru,
                    name_hy: form.name_hy,
                    description_en: form.description_en,
                    description_ru: form.description_ru,
                    description_hy: form.description_hy,
                    body_en: form.body_en,
                    body_ru: form.body_ru,
                    body_hy: form.body_hy,
                    price: form.price,
                    original_price: form.original_price === '' || form.original_price == null ? null : form.original_price,
                    discount: form.discount === '' || form.discount == null ? null : form.discount,
                    category_id: form.category_id === '' || form.category_id == null ? null : form.category_id,
                    total_lessons: form.total_lessons === '' || form.total_lessons == null ? null : Number(form.total_lessons),
                    duration: form.duration === '' || form.duration == null ? null : Number(form.duration),
                    is_private: !!form.is_private,
                };
                const response = await api.post('admin/courses', payload);
                onClose(true, response?.data?.data || null);
                return;
            } else {
                const payload = {
                    name_en: form.name_en,
                    name_ru: form.name_ru,
                    name_hy: form.name_hy,
                    description_en: form.description_en,
                    description_ru: form.description_ru,
                    description_hy: form.description_hy,
                    body_en: form.body_en,
                    body_ru: form.body_ru,
                    body_hy: form.body_hy,
                    price: form.price,
                    original_price: form.original_price === '' || form.original_price == null ? null : form.original_price,
                    discount: form.discount === '' || form.discount == null ? null : form.discount,
                    category_id: form.category_id === '' || form.category_id == null ? null : form.category_id,
                    total_lessons: form.total_lessons === '' || form.total_lessons == null ? null : Number(form.total_lessons),
                    duration: form.duration === '' || form.duration == null ? null : Number(form.duration),
                    is_private: !!form.is_private,
                };
                await api.put(`admin/courses/${row.id}`, payload);
            }

            onClose(true);
        } catch (e) {
            if (e.errors) setErrors(e.errors);
            else if (e.response?.data?.errors) setErrors(e.response.data.errors);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            const newForm = initialForm();
            languages.forEach(lang => {
                newForm[`name_${lang}`] = row[`name_${lang}`] ?? '';
                newForm[`description_${lang}`] = row[`description_${lang}`] ?? '';
                newForm[`body_${lang}`] = row[`body_${lang}`] ?? '';
            });
            newForm.price = row.price ?? '';
            newForm.original_price = row.original_price ?? '';
            newForm.discount = row.discount ?? '';
            newForm.category_id = row.category_id ?? '';
            newForm.total_lessons = row.total_lessons ?? '';
            newForm.duration = row.duration ?? '';
            newForm.is_private = !!row.is_private;
            setForm(newForm);
            setImagePreview(row.thumbnail || null);
            setDeleteThumbnail(false);
        } else {
            setForm(initialForm());
            setImagePreview(null);
            setDeleteThumbnail(false);
        }
        setErrors({});
        setSaving(false);
        setActiveTab(0);
    }, [row, open]);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose();
            }}
            disableEscapeKeyDown
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{!row ? "Create Course" : "Edit Course"}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 3, mt: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {errorMessages.length > 0 && (
                            <Alert severity="error">
                                {errorMessages.slice(0, 3).join(' | ')}
                            </Alert>
                        )}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                                {languages.map((lang) => (
                                    <Tab
                                        key={lang}
                                        label={languageLabels[lang]}
                                        sx={tabHasErrors(lang) ? { color: 'error.main' } : undefined}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        <FormControl error={!!errors[`name_${currentLanguage}`]?.[0]}>
                            <FormLabel>Name ({languageLabels[currentLanguage]})</FormLabel>
                            <OutlinedInput
                                name={`name_${currentLanguage}`}
                                fullWidth
                                value={form[`name_${currentLanguage}`] ?? ''}
                                onChange={handleChange}
                            />
                            {errors[`name_${currentLanguage}`] && (
                                <FormHelperText sx={{ m: 0.5 }}>{errors[`name_${currentLanguage}`][0]}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!errors[`description_${currentLanguage}`]?.[0]}>
                            <FormLabel>Description ({languageLabels[currentLanguage]})</FormLabel>
                            <OutlinedInput
                                name={`description_${currentLanguage}`}
                                fullWidth
                                value={form[`description_${currentLanguage}`] ?? ''}
                                onChange={handleChange}
                            />
                            {errors[`description_${currentLanguage}`] && (
                                <FormHelperText sx={{ m: 0.5 }}>{errors[`description_${currentLanguage}`][0]}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!errors[`body_${currentLanguage}`]?.[0]} fullWidth>
                            <FormLabel sx={{ mb: 1 }}>Content ({languageLabels[currentLanguage]})</FormLabel>
                            <QuillEditor
                                key={`body-editor-${currentLanguage}`}
                                value={form[`body_${currentLanguage}`] ?? ''}
                                onChange={(value) => handleBodyChange(currentLanguage, value)}
                                placeholder={`Write ${languageLabels[currentLanguage]} content...`}
                            />
                            {errors[`body_${currentLanguage}`] && (
                                <FormHelperText sx={{ m: 0.5 }}>{errors[`body_${currentLanguage}`][0]}</FormHelperText>
                            )}
                        </FormControl>

                    </Box>
                    <Box sx={{ width: { xs: '100%', md: 260 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl error={!!errors.thumbnail?.[0]} fullWidth>
                                <FormLabel>{row ? 'Thumbnail' : 'Thumbnail *'}</FormLabel>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            width: 180,
                                            height: 120,
                                            border: imagePreview ? 'none' : '1px dashed',
                                            borderColor: imagePreview ? 'transparent' : 'divider',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: imagePreview ? 'transparent' : 'background.default',
                                            position: 'relative',
                                        }}
                                    >
                                        {imagePreview ? (
                                            <>
                                                <Avatar
                                                    variant="rounded"
                                                    src={imagePreview}
                                                    sx={{ width: 180, height: 120 }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'rgba(255,255,255,0.9)' }}
                                                    onClick={handleRemoveImage}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <FormHelperText sx={{ m: 0, textAlign: 'center' }}>
                                                No image uploaded
                                            </FormHelperText>
                                        )}
                                    </Box>
                                    <Button variant="outlined" component="label">
                                        {imagePreview ? 'Change' : 'Upload'}
                                        <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                    </Button>
                                </Box>
                                {errors.thumbnail && <FormHelperText sx={{ m: 0.5 }}>{errors.thumbnail[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl error={!!errors.category_id?.[0]} fullWidth>
                            <FormLabel>Category</FormLabel>                               
                                <Select
                                    labelId="course-category-label"
                                    name="category_id"
                                    value={form.category_id ?? ''}
                                    onChange={handleChange}                                  
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {categories.map((cat) => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.title_en || cat.title_ru || cat.title_hy || cat.id}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.category_id && <FormHelperText sx={{ m: 0.5 }}>{errors.category_id[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl error={!!errors.original_price?.[0]} fullWidth>
                                <FormLabel>Original price *</FormLabel>
                                <OutlinedInput
                                    name="original_price"
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={form.original_price}
                                    onChange={handlePricingChange}
                                    startAdornment={<InputAdornment position="start">֏</InputAdornment>}
                                />
                                {errors.original_price && <FormHelperText sx={{ m: 0.5 }}>{errors.original_price[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl error={!!errors.discount?.[0]} fullWidth>
                                <FormLabel>Discount %</FormLabel>
                                <OutlinedInput
                                    name="discount"
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                    value={form.discount}
                                    onChange={handlePricingChange}
                                />
                                {errors.discount && <FormHelperText sx={{ m: 0.5 }}>{errors.discount[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl error={!!errors.price?.[0]} fullWidth>
                                <FormLabel>Price *</FormLabel>
                                <OutlinedInput
                                    name="price"
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={form.price}
                                    onChange={handlePricingChange}
                                    startAdornment={<InputAdornment position="start">֏</InputAdornment>}
                                />
                                {errors.price && <FormHelperText sx={{ m: 0.5 }}>{errors.price[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl error={!!errors.total_lessons?.[0]} fullWidth>
                                <FormLabel>Total Lessons *</FormLabel>
                                <OutlinedInput
                                    name="total_lessons"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={form.total_lessons}
                                    onChange={handleChange}
                                />
                                {errors.total_lessons && <FormHelperText sx={{ m: 0.5 }}>{errors.total_lessons[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl error={!!errors.duration?.[0]} fullWidth>
                                <FormLabel>Duration (days) *</FormLabel>
                                <OutlinedInput
                                    name="duration"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={form.duration}
                                    onChange={handleChange}
                                />
                                {errors.duration && <FormHelperText sx={{ m: 0.5 }}>{errors.duration[0]}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!form.is_private}
                                            onChange={(e) =>
                                                setForm((prev) => ({ ...prev, is_private: !!e.target.checked }))
                                            }
                                        />
                                    }
                                    label="Private course"
                                />
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button sx={{ color: '#fff' }} variant="contained" onClick={handleSubmit} disabled={saving}>
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
